import { AvatarSize } from "@components/Avatar";
import {
  Button,
  ButtonSize,
  ButtonRole,
  ButtonTargetKind,
} from "@components/Button";
import { Card } from "@components/Card";
import { TagIllustrationOld } from "@components/TagIllustration";
import { Feed, NoMoreFeedItemsCallbackProp } from "@components/feed";
import { maxWidthFeedCardCss } from "@components/feed/shared";
import { MasonryListProps } from "@components/layout/MasonryList";
import { PageSection } from "@components/layout/PageSection";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FeedItemResponse,
  UserResponse,
  UserTagResponse,
} from "@every.org/common/src/codecs/entities";
import { CAUSE_PASTEL_BG_PALETTE } from "@every.org/common/src/display/palette";
import { spacing } from "@every.org/common/src/display/spacing";
import { FeedPage } from "@every.org/common/src/entity/types";
import {
  getRoutePath,
  URLFormat,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";
import { SkipInt, TakeInt } from "@every.org/common/src/routes/index";
import { getUserCausesRouteSpec } from "@every.org/common/src/routes/users";

import { Link } from "src/components/Link";
import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { fetchLikesFeed } from "src/context/DonationsContext/actions";
import { TagsContext } from "src/context/TagContext";
import { useTag } from "src/context/TagContext/hooks";
import { TagActionType, TagFetchStatus } from "src/context/TagContext/types";
import { lightBgThemeCss } from "src/theme/color";
import { BORDER_RADIUS } from "src/theme/common";
import { verticalStackCss, horizontalStackCss } from "src/theme/spacing";
import { queryApi } from "src/utility/apiClient";

const StyledCard = styled(Card)`
  ${maxWidthFeedCardCss};
  padding: ${spacing.xs};
  padding-bottom: ${spacing.m};
  ${verticalStackCss.m}
`;

const StyledContainer = styled.div`
  ${lightBgThemeCss};
  flex-direction: column;
  border-radius: ${BORDER_RADIUS};
  overflow: hidden; /* add rounded corners to child img */
  position: relative;
  padding: ${spacing.s};
  ${horizontalStackCss.m};
  flex-direction: row;
  align-items: center;
`;

interface LikesTabProps extends NoMoreFeedItemsCallbackProp {
  user: UserResponse;
  likesFeed?: FeedItemResponse[];
}

export const LikesTab = ({ user, onNoMoreItems, likesFeed }: LikesTabProps) => {
  const loggedInUser = useLoggedInUserOrUndefined();
  const isOwnProfile = user.id === loggedInUser?.id;

  if (!user) {
    return null;
  }

  return (
    <LikesFeed
      likesFeed={likesFeed}
      user={user}
      isOwnFeed={isOwnProfile}
      name={user.firstName}
      onNoMoreItems={onNoMoreItems}
    />
  );
};

interface LikesFeedProps extends NoMoreFeedItemsCallbackProp {
  user: UserResponse;
  name?: string | null;
  isOwnFeed: boolean;
  likesFeed?: FeedItemResponse[];
}

/**
 * Feed of donations from a given user.
 */
const LikesFeed: React.FCC<LikesFeedProps> = ({
  user,
  name,
  isOwnFeed,
  onNoMoreItems,
  likesFeed,
}) => {
  const noItemsDisplay = (
    <h3>
      {isOwnFeed ? (
        <span>
          {"You haven't liked any nonprofits or donations yet. "}
          <Link
            data-tname="UserRecentDonationsDonateLink"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.HOME,
            })}
          >
            Find your first!
          </Link>
        </span>
      ) : (
        `${name || "This person"} hasn't liked anything yet.`
      )}
    </h3>
  );

  const [tagLikes, setTagLikes] = useState<UserTagResponse[]>();
  const { dispatchTagsAction } = useContext(TagsContext);
  useEffect(() => {
    async function getTagLikes() {
      const result = await queryApi(getUserCausesRouteSpec, {
        routeTokens: { id: user.id },
        body: {},
        queryParams: { take: 20 as TakeInt, skip: 0 as SkipInt },
      });
      const tagLikes = result.tags;
      if (tagLikes) {
        dispatchTagsAction({
          type: TagActionType.ADD_TAGS,
          data: result.nonprofitTags,
        });
      }
      setTagLikes(tagLikes);
    }
    getTagLikes();
  }, [dispatchTagsAction, user.id]);

  const addLikedTagItems = useMemo(() => {
    return tagLikes
      ? ({ items }: { items: MasonryListProps["items"] }) => [
          ...tagLikes.map((tagLike: UserTagResponse) => {
            return {
              key: tagLike.nonprofitTagId,
              elem: <UserLikedTagCard {...tagLike} />,
            };
          }),
          ...items,
        ]
      : undefined;
  }, [tagLikes]);

  return (
    <PageSection>
      <Feed
        initialItems={likesFeed}
        page={FeedPage.USER_LIKES}
        loadMoreItems={useCallback(
          (params) => fetchLikesFeed({ userId: user.id, ...params }),
          [user.id]
        )}
        noItemsDisplay={noItemsDisplay}
        transformMasonryItems={addLikedTagItems}
        onNoMoreItems={onNoMoreItems}
      />
    </PageSection>
  );
};

const UserLikedTagCard = ({
  nonprofitTagId: id,
  likeCount,
  giftCount,
}: UserTagResponse) => {
  const tag = useTag({ id });

  if (
    tag === TagFetchStatus.FETCHING_TAG ||
    tag === TagFetchStatus.TAG_NOT_FOUND
  ) {
    return null;
  }

  const { tagName, causeCategory, tagImageCloudinaryId } = tag;

  return (
    <StyledCard>
      <StyledContainer
        css={{
          backgroundColor: CAUSE_PASTEL_BG_PALETTE[causeCategory].pastel40,
        }}
      >
        <TagIllustrationOld
          tagName={tagName}
          tagImageCloudinaryId={tagImageCloudinaryId}
          size={AvatarSize.MEDIUM}
          disableLink
          causeCategory={tag.causeCategory}
        />
        <div>
          <h3>{tag.title}</h3>
          <div css={horizontalStackCss.m}>
            {giftCount > 0 && (
              <p>
                <span css={{ opacity: 0.7 }}>Gifts: </span>
                {giftCount}
              </p>
            )}
            {likeCount > 0 && (
              <p>
                <span css={{ opacity: 0.7 }}>Likes: </span>
                {likeCount}
              </p>
            )}
          </div>
        </div>
      </StyledContainer>
      <div
        css={css`
          padding: 0 ${spacing.xs};
          display: flex;
          justify-content: flex-end;
        `}
      >
        <Button
          data-tname="discoverTag-Button"
          size={ButtonSize.SMALL}
          role={ButtonRole.PRIMARY}
          onClick={{
            kind: ButtonTargetKind.LINK,
            to: getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.NONPROFIT_OR_CAUSE,
              tokens: { nonprofitSlug: tagName },
            }),
          }}
        >
          Discover
        </Button>
      </div>
    </StyledCard>
  );
};
